import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import useApi from '@/hooks/useApi';

import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TInteractionScroll } from '@/services/requests/Interactions/types';
import { TRecommendation as TRecommendation1 } from '@/services/requests/Recommendation/types/template1';
import { TRecommendation as TRecommendation2 } from '@/services/requests/Recommendation/types/template2';
import { TRecommendation as TRecommendation3 } from '@/services/requests/Recommendation/types/template3';
import { TRecommendation as TRecommendation4 } from '@/services/requests/Recommendation/types/template4';
import { TRecommendation as TRecommendation5 } from '@/services/requests/Recommendation/types/template5';
import { TRecommendation as TRecommendation6 } from '@/services/requests/Recommendation/types/template6';
import { TRecommendation as TRecommendation7 } from '@/services/requests/Recommendation/types/template7';

import { getVisibleCardsIdByElement } from '@/helpers/HtmlHelper/getVisibleCardsIdByElement';
import { getValue } from '@/helpers/GetValueHelper';

import {
  getCardWidth,
  checkScrollLimit as checkScrollLimitUtil,
} from './utlis';

export const useSlider = (
  data:
    | TRecommendation1
    | TRecommendation2
    | TRecommendation3
    | TRecommendation4
    | TRecommendation5
    | TRecommendation6
    | TRecommendation7,
  tabName: string,
  tabActive: boolean,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  layout = '',
  cardClassName = 'vitrine-template1__card',
) => {
  const { id, cards } = data;
  const containerRef = useRef<HTMLDivElement>(null);

  const { Api } = useApi();

  const [sliderState, setSliderState] = useState(() => ({
    scrollStep: parseInt(
      getValue(data, 'config.cards_scroll_by', '1').toString(),
      10,
    ),
    numberOfCards: cards?.length,
    cardsWidth: 0,
    cardsInView: 0,
    width: 0,
    scroll: 0,
    current: 1,
  }));

  async function moveSlider(direction: string) {
    if (!containerRef.current) {
      return;
    }

    const { scroll, cardsWidth, scrollStep } = sliderState;
    let newScroll = scroll;

    if (direction === 'left') {
      newScroll -= cardsWidth * scrollStep;
    }

    if (direction === 'right') {
      newScroll += cardsWidth * scrollStep;
    }

    setSliderState({
      ...sliderState,
      scroll: newScroll,
    });

    containerRef.current.scrollLeft = newScroll;

    await dispatchInteraction<TInteractionScroll>(Api, 'scroll', {
      recommendationId: id,
      cards: getVisibleCardsIdByElement(
        containerRef.current as HTMLElement,
        'scroll',
        `.${cardClassName}`,
      ),
      tabName,
    });
  }

  async function moveSliderTo(index: number) {
    if (!containerRef.current) {
      return;
    }

    const { scroll, cardsWidth } = sliderState;
    let newScroll = scroll;
    newScroll = cardsWidth * index;

    setSliderState({
      ...sliderState,
      scroll: newScroll,
    });

    containerRef.current.scrollLeft = newScroll;

    await dispatchInteraction<TInteractionScroll>(Api, 'scroll', {
      recommendationId: id,
      cards: getVisibleCardsIdByElement(
        containerRef.current as HTMLElement,
        'scroll',
        `.${cardClassName}`,
      ),
      tabName,
    });
  }

  useEffect(() => {
    containerRef.current?.addEventListener('scroll', e => {
      const { currentTarget } = e as Event & {
        currentTarget: {
          scrollWidth: number;
          clientWidth: number;
          scrollLeft: number;
        };
      };
      if (currentTarget) {
        const totalWidth = currentTarget.scrollWidth;
        const visibleWidth = currentTarget.clientWidth;
        const scrollAmount = currentTarget.scrollLeft;

        const scrollPercent =
          (scrollAmount / (totalWidth - visibleWidth)) * 100;

        const percentItem = 100 / cards?.length;

        const step = Math.floor(scrollPercent / percentItem + 1);
        setSliderState(prev => ({
          ...prev,
          current: step >= cards?.length ? cards?.length : step,
        }));
      }
    });
  }, [cards?.length, containerRef]);

  useLayoutEffect(() => {
    if (tabActive) {
      const cardsWidth = getCardWidth(containerRef, cardClassName);
      const cardsInView = getVisibleCardsIdByElement(
        containerRef.current as HTMLElement,
        'scroll',
        `.${cardClassName}`,
      ).length;
      const numberOfCards = cards?.length;

      setSliderState(prev => ({
        ...prev,
        numberOfCards,
        cardsWidth,
        cardsInView,
        scroll: 0,
      }));
    }
  }, [cardClassName, cards?.length, tabActive]);

  return {
    sliderState,
    moveSlider,
    moveSliderTo,
    containerRef,
    checkScrollLimit: () => {
      return checkScrollLimitUtil(
        sliderState.cardsWidth,
        sliderState.scroll,
        sliderState.numberOfCards,
        sliderState.cardsInView,
      );
    },
  };
};
